import { useEffect, useMemo, useRef, useState } from "react";
import { Boxes, ScanBarcode, Lock, Shuffle, CheckCircle2, AlertTriangle, MapPin, Trash2, ClipboardList, PackageCheck, Delete, CornerDownLeft, Warehouse, ListChecks, DollarSign, Eye, MoreVertical, X, List, FileText, PackagePlus, Eraser } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { beep, fmtDT } from "@/lib/sound";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const normalizarEtiquetaCaixa = (valor) => {
  const texto = String(valor || "").trim();
  const digitos = texto.replace(/\D/g, "");
  return digitos.length === 14 ? digitos : texto;
};

const ehEtiquetaCaixa = (valor) => String(valor || "").replace(/\D/g, "").length === 14;

const normalizarEan = (valor) => String(valor || "").replace(/\D/g, "");

// Exibe apenas o número da caixa (ex.: "00013535206748" → "13") sem alterar
// o identificador real usado nas chamadas de API.
const numeroCaixaDisplay = (valor) => {
  const digitos = String(valor || "").replace(/\D/g, "");
  if (digitos.length === 14) {
    const n = parseInt(digitos.slice(0, 5), 10);
    if (!Number.isNaN(n)) return String(n);
  }
  return String(valor || "").trim();
};

function Keypad({ onDigit, onBack, onEnter, testid }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testid}>
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
        <button key={k} data-testid={`conf-key-${k}`} onClick={() => onDigit(k)} className="h-12 rounded-md bg-white border border-[#D9E3DC] text-xl font-bold text-[#0F2A1F] hover:bg-[#F1F5F2] active:bg-[#EAF7EF]">{k}</button>
      ))}
      <button data-testid="conf-key-enter" onClick={onEnter} className="h-12 rounded-md bg-[#0F6B3A] hover:bg-[#0B5530] text-white flex items-center justify-center"><CornerDownLeft className="w-5 h-5" /></button>
      <button data-testid="conf-key-0" onClick={() => onDigit("0")} className="h-12 rounded-md bg-white border border-[#D9E3DC] text-xl font-bold text-[#0F2A1F] hover:bg-[#F1F5F2]">0</button>
      <button data-testid="conf-key-back" onClick={onBack} className="h-12 rounded-md bg-white border border-[#D9E3DC] flex items-center justify-center hover:bg-[#F1F5F2]"><Delete className="w-5 h-5 text-[#0F2A1F]" /></button>
    </div>
  );
}

// Aplica localmente a delta de uma bipagem no `rec` sem refetch do backend.
// Retorna um NOVO objeto rec para preservar imutabilidade e disparar re-render.
const aplicarDeltaBipagem = (rec, data) => {
  if (!rec || !data?.delta) return rec;
  const d = data.delta;
  const conf = { ...(rec.conferencia || { caixas: [] }) };
  const caixas = [...(conf.caixas || [])];
  let idx = caixas.findIndex((c) => c.numero === d.caixa_numero);
  if (idx >= 0) {
    const cx = caixas[idx];
    const itens = { ...(cx.itens || {}) };
    itens[d.codigo_produto] = (itens[d.codigo_produto] || 0) + d.qtd_adicionada;
    caixas[idx] = { ...cx, itens, tipo: d.tipo };
  } else if (data.caixa_atualizada) {
    caixas.push(data.caixa_atualizada);
  }
  conf.caixas = caixas;
  const itens = (rec.itens || []).map((it) =>
    it.codigo === d.codigo_produto
      ? { ...it, qtd_conferida: d.total_item_conferido, divergencia: d.total_item_conferido - it.qtd }
      : it,
  );
  // Recalcula contadores leves de mercadoria (bipadas ↔ faltantes) sem tocar em outros KPIs.
  const bipadas = [...new Set(caixas.map((c) => String(c.numero)))].sort();
  const controle_mercadoria = {
    ...(rec.controle_mercadoria || {}),
    bipadas,
    total_bipado: bipadas.length,
  };
  return { ...rec, conferencia: conf, itens, controle_mercadoria };
};

// Aplica atualização de conferência (mercadoria/volumes) sem recalcular o resto.
const aplicarNovaCaixa = (rec, data) => {
  if (!rec) return rec;
  const patch = { ...rec };
  if (data.conferencia_mercadoria) {
    patch.conferencia = data.conferencia_mercadoria;
  }
  if (data.conferencia_volumes) {
    patch.conferencia_volumes = data.conferencia_volumes;
  }
  return patch;
};

export function ConferenciaCaixas({ rec, onChanged, onLimpar, onApagar }) {
  const [caixa, setCaixa] = useState("");
  const [caixaAtiva, setCaixaAtiva] = useState("");
  const [etiquetaAtiva, setEtiquetaAtiva] = useState("");
  const [modo, setModo] = useState("fechada");
  const [scan, setScan] = useState("");
  const [qtd, setQtd] = useState("");
  const [buscaCaixa, setBuscaCaixa] = useState("");
  const [msg, setMsg] = useState(null);
  const [count, setCount] = useState(0);
  const [showRel, setShowRel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fecharBox, setFecharBox] = useState(null);
  const [detalheBox, setDetalheBox] = useState(null);
  const [ultimoFechado, setUltimoFechado] = useState(null); // { numero, destino, endereco, itens }
  const [menuOpen, setMenuOpen] = useState(null); // "volumes" | "enderecamentos" | "validacoes" | "irregularidades"
  const scanRef = useRef(null);
  const caixaRef = useRef(null);
  const conf = rec.conferencia || { caixas: [] };
  const boxes = conf.caixas || [];
  const abertas = boxes.filter((b) => !b.fechada);
  const fechadas = boxes.filter((b) => b.fechada);
  const itemNome = (cod) => (rec.itens.find((i) => i.codigo === cod)?.descricao) || "";
  const boxAtiva = boxes.find((b) => b.numero === caixaAtiva);
  const codigosNF = useMemo(() => new Set(rec.itens.map((i) => i.codigo)), [rec.itens]);
  const caixaDivergente = (b) => {
    if (b?.validacao && b.validacao.diferenca !== undefined && b.validacao.diferenca !== 0) return true;
    if (Object.keys(b?.itens || {}).some((c) => !codigosNF.has(c))) return true;
    return false;
  };

  const selecionarCaixa = (box) => {
    setCaixa(box.numero);
    setCaixaAtiva(box.numero);
    setEtiquetaAtiva(box.etiqueta || box.numero);
    setModo(box.tipo || "fechada");
    setMsg(null);
    setTimeout(() => scanRef.current?.focus(), 50);
  };

  const codigoEhProduto = async (codigo) => {
    const ean = normalizarEan(codigo);
    const produtoNaNf = rec.itens.some(
      (item) => item.ean && normalizarEan(item.ean) === ean,
    );
    if (produtoNaNf) return true;
    try {
      const { data } = await api.get(`/recebimento/produto/ean/${encodeURIComponent(ean)}`);
      return data.encontrado;
    } catch {
      // Na dúvida, mantém o código como produto para não abrir uma caixa indevidamente.
      return true;
    }
  };

  const trocarCaixaPelaEtiqueta = async (etiqueta) => {
    const existente = boxes.find(
      (box) => box.etiqueta === etiqueta || box.numero === etiqueta,
    );
    if (existente) {
      if (existente.fechada) {
        toast.error(`Caixa ${numeroCaixaDisplay(existente.numero)} já foi endereçada`);
        return;
      }
      selecionarCaixa(existente);
      beep("scan-ok");
      toast.success(`Caixa alterada para ${numeroCaixaDisplay(existente.numero)}`);
      return;
    }

    try {
      const { data } = await api.post(
        `/recebimento/${rec.id}/conferencia/mercadoria/caixa`,
        { etiqueta },
      );
      // Atualização otimista: aplica a caixa retornada sem re-analyze completo.
      onChanged?.((prev) => aplicarNovaCaixa(prev, data));
      selecionarCaixa(data.caixa);
      beep("scan-ok");
      toast.success(`Caixa alterada para ${numeroCaixaDisplay(data.caixa.numero)}`);
    } catch (err) {
      if (err.response?.status !== 409) {
        beep("alert");
        toast.error(err.response?.data?.detail || "Não foi possível trocar de caixa");
        return;
      }
      try {
        const { data } = await api.get(`/recebimento/${rec.id}`);
        const box = (data.conferencia?.caixas || []).find(
          (item) => item.etiqueta === etiqueta || item.numero === etiqueta,
        );
        if (!box) {
          toast.error("Caixa não encontrada na conferência de mercadoria");
          return;
        }
        if (box.fechada) {
          toast.error(`Caixa ${numeroCaixaDisplay(box.numero)} já foi endereçada`);
          return;
        }
        selecionarCaixa(box);
        // Sync ao vivo do estado após corrida — 409 devolve a NF completa.
        onChanged?.(data);
        beep("scan-ok");
        toast.success(`Caixa alterada para ${numeroCaixaDisplay(box.numero)}`);
      } catch {
        beep("alert");
        toast.error("Não foi possível atualizar a caixa ativa");
      }
    }
  };

  const iniciar = async () => {
    const n = normalizarEtiquetaCaixa(caixa);
    if (!n) { toast.error("Bipe/digite a etiqueta da caixa"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/recebimento/${rec.id}/conferencia/mercadoria/caixa`,
        { etiqueta: n },
      );
      beep("scan-ok");
      setCaixa(data.caixa.numero);
      setCaixaAtiva(data.caixa.numero);
      setEtiquetaAtiva(n);
      setMsg(null);
      onChanged?.((prev) => aplicarNovaCaixa(prev, data));
      setTimeout(() => scanRef.current?.focus(), 50);
    } catch (err) {
      beep("alert");
      toast.error(err.response?.data?.detail || "Etiqueta de caixa inválida");
    } finally { setBusy(false); }
  };

  const bipar = async () => {
    const code = scan.trim();
    if (!caixaAtiva) { toast.error("Bipe a etiqueta da caixa primeiro"); return; }
    if (!code) return;
    if (ehEtiquetaCaixa(code)) {
      setBusy(true);
      let produtoConhecido = true;
      try {
        produtoConhecido = await codigoEhProduto(code);
        if (!produtoConhecido) {
          await trocarCaixaPelaEtiqueta(normalizarEtiquetaCaixa(code));
        }
      } finally {
        setBusy(false);
      }
      if (!produtoConhecido) {
        setScan("");
        setQtd("");
        scanRef.current?.focus();
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/recebimento/${rec.id}/conferencia/bipar`, { caixa: caixaAtiva, tipo: modo, codigo: code, qtd: modo === "sortida" ? (Number(qtd) || 1) : undefined });
      beep(data.nivel === "sobra" ? "alert" : "scan-ok");
      setMsg(data); setCount((c) => c + 1);
      // Atualização local instantânea — sem esperar recomputo pesado do backend.
      onChanged?.((prev) => aplicarDeltaBipagem(prev, data));
    } catch (err) { beep("alert"); toast.error(err.response?.data?.detail || "Erro ao bipar"); }
    finally { setBusy(false); setScan(""); setQtd(""); scanRef.current?.focus(); }
  };

  const confirmarFechar = async (payload, forcarSubstituicao = false) => {
    const b = fecharBox;
    setBusy(true);
    try {
      const { data } = await api.post(`/recebimento/${rec.id}/conferencia/fechar`, { caixa: b.numero, ...payload, substituir: forcarSubstituicao });
      const cxFechada = (data.conferencia?.caixas || []).find((c) => c.numero === b.numero);
      const endInfo = cxFechada?.endereco || (payload.itens?.[0]?.endereco) || "—";
      setUltimoFechado({ numero: b.numero, destino: payload.destino, endereco: endInfo, itens: payload.itens || [] });
      toast.success(`Caixa ${numeroCaixaDisplay(b.numero)} endereçada → ${endInfo}`);
      beep("scan-ok");
      if (b.numero === caixaAtiva) { setCaixaAtiva(""); setCaixa(""); setMsg(null); setTimeout(() => caixaRef.current?.focus(), 50); }
      setFecharBox(null);
      onChanged?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.code === "ENDERECO_OCUPADO") {
        setBusy(false);
        const ok = window.confirm(
          `Já existe o produto ${detail.codigo_atual} ${detail.nome_atual ? "(" + detail.nome_atual + ")" : ""} no endereço ${detail.endereco}.\n\nDeseja substituí-lo pela caixa ${numeroCaixaDisplay(b.numero)}?`,
        );
        if (ok) return confirmarFechar(payload, true);
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Erro ao fechar caixa");
    }
    finally { setBusy(false); }
  };

  const remover = async (b) => {
    if (!window.confirm(`Remover Caixa ${numeroCaixaDisplay(b.numero)} da conferência?`)) return;
    try { const { data } = await api.delete(`/recebimento/${rec.id}/conferencia/caixa/${encodeURIComponent(b.numero)}`); onChanged?.(data); toast.success("Caixa removida"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const divergentes = rec.itens.filter((i) => i.qtd_conferida !== i.qtd);
  const controle = rec.controle_mercadoria || {
    total_esperado: 0,
    total_bipado: 0,
    faltantes: [],
  };
  const extras = rec.conferencia_extras || [];
  const conferidosOk = rec.itens.filter((i) => i.qtd_conferida === i.qtd).length;
  const totalConf = rec.itens.reduce((s, i) => s + (i.qtd_conferida || 0), 0);
  const financeiro = useMemo(() => {
    const cats = { validados: { est: 0, cont: 0 }, faltando: { est: 0, cont: 0 }, sobrando: { est: 0, cont: 0 }, zero: { est: 0, cont: 0 } };
    rec.itens.forEach((i) => {
      const v = Number(i.vunit || 0);
      const est = (i.qtd || 0) * v;
      const cont = (i.qtd_conferida || 0) * v;
      const d = (i.qtd_conferida || 0) - (i.qtd || 0);
      if (i.qtd_conferida === 0) { cats.zero.est += est; cats.zero.cont += cont; }
      else if (d === 0) { cats.validados.est += est; cats.validados.cont += cont; }
      else if (d < 0) { cats.faltando.est += est; cats.faltando.cont += cont; }
      else { cats.sobrando.est += est; cats.sobrando.cont += cont; }
    });
    const total = { est: Object.values(cats).reduce((s, c) => s + c.est, 0), cont: Object.values(cats).reduce((s, c) => s + c.cont, 0) };
    return { cats, total };
  }, [rec.itens]);
  const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unBox = boxAtiva ? Object.values(boxAtiva.itens).reduce((s, q) => s + q, 0) : 0;
  const cxLabel = (lst) => (lst || []).map((c) => `${numeroCaixaDisplay(c.numero)}:${c.qtd}${c.fechada ? "(F)" : ""}`).join(" · ");
  const enderecoDe = (cod) => { const it = rec.itens.find((i) => i.codigo === cod); return it?.enderecos_atuais?.[0] || ""; };
  const exportar = async () => {
    try {
      const res = await api.get(`/recebimento/${rec.id}/conferencia/relatorio.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Conferencia_NF${rec.nf_numero}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao exportar relatório"); }
  };
  const exportarPdf = async () => {
    try {
      const res = await api.get(`/recebimento/${rec.id}/conferencia/relatorio.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Conferencia_NF${rec.nf_numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao gerar relatório PDF"); }
  };

  return (
    <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 space-y-4" data-testid="rec-conferencia">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[#0F2A1F] font-bold"><Boxes className="w-5 h-5 text-[#0F6B3A]" /> Conferência de Mercadoria</div>
        <div className="flex items-center gap-3 text-xs font-mono text-[#5A6B62]">
          <span data-testid="conf-progress-caixas">Caixas bipadas: <b className="text-[#0F2A1F]">{controle.total_bipado}</b>{controle.total_esperado ? ` / ${controle.total_esperado}` : ""}</span>
          <span data-testid="conf-caixas-faltantes">Faltam: <b className="text-[#B91C1C]">{controle.faltantes.length}</b></span>
          <span>Itens OK: <b className="text-[#047857]">{conferidosOk}</b>/{rec.itens.length}</span>
          <button data-testid="conf-relatorio-toggle" onClick={() => setShowRel(!showRel)} className="inline-flex items-center gap-1 text-[#0F6B3A] font-bold hover:underline"><ClipboardList className="w-3.5 h-3.5" /> Relatório</button>
        </div>
      </div>

      {!caixaAtiva ? (
        /* PASSO 1 — bipar a etiqueta da caixa (localização) */
        <div className="bg-[#0B1F17] rounded-lg p-3 space-y-3" data-testid="conf-passo1">
          <label className="text-[10px] uppercase tracking-widest text-[#2DC26B] font-bold flex items-center gap-1"><ScanBarcode className="w-3.5 h-3.5" /> Passo 1 · Bipe a etiqueta da caixa (localização)</label>
          <div className="flex gap-2">
            <input ref={caixaRef} data-testid="conf-caixa-input" value={caixa} onChange={(e) => setCaixa(normalizarEtiquetaCaixa(e.target.value))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); iniciar(); } }} autoFocus
              placeholder="Bipe a etiqueta de 14 dígitos da caixa" className="flex-1 min-w-0 h-14 bg-white text-[#0F2A1F] font-mono text-xl rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-[#2DC26B]" />
            <button data-testid="conf-iniciar-btn" disabled={busy} onClick={iniciar} className="h-14 px-5 bg-[#2DC26B] hover:bg-[#22A85B] text-white font-bold rounded-md flex items-center gap-2 shrink-0 disabled:opacity-60"><ScanBarcode className="w-4 h-4" /> Iniciar</button>
          </div>
          <Keypad testid="conf-keypad1" onDigit={(d) => setCaixa((v) => v + d)} onBack={() => setCaixa((v) => v.slice(0, -1))} onEnter={iniciar} />
        </div>
      ) : (
        /* PASSO 2 — modelo mobile: Último Registro + código + BUSCAR + teclado */
        <div className="space-y-3" data-testid="conf-passo2">
          <div className="bg-[#0B1F17] border border-[#2DC26B] rounded-md px-3 py-2 space-y-2" data-testid="conf-caixa-ativa">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2 text-sm text-white">
                <Warehouse className="h-4 w-4 shrink-0 text-[#2DC26B]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#2DC26B]">
                  Caixa
                </span>
                <span className="max-w-[210px] truncate font-mono text-lg font-extrabold text-[#2DC26B] sm:max-w-none sm:text-xl">
                  {numeroCaixaDisplay(caixaAtiva)}
                </span>
                <span className="hidden text-xs text-slate-400 sm:inline">
                  · Etiqueta {etiquetaAtiva || caixaAtiva} · {unBox} un conferidas
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                <button data-testid="conf-trocar-caixa" onClick={() => { setCaixaAtiva(""); setCaixa(""); setMsg(null); setTimeout(() => caixaRef.current?.focus(), 50); }} className="text-xs font-bold text-[#F2B01E] hover:underline">Trocar caixa</button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="conf-menu-btn" className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><MoreVertical className="w-4 h-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid="conf-menu">
                    <DropdownMenuItem data-testid="conf-menu-volumes" onClick={() => setMenuOpen("volumes")}><Boxes className="w-4 h-4 mr-2 text-[#0F6B3A]" /> Ver volumes</DropdownMenuItem>
                    <DropdownMenuItem data-testid="conf-menu-enderecamentos" onClick={() => setMenuOpen("enderecamentos")}><MapPin className="w-4 h-4 mr-2 text-[#0F6B3A]" /> Ver endereçamentos</DropdownMenuItem>
                    <DropdownMenuItem data-testid="conf-menu-validacoes" onClick={() => setMenuOpen("validacoes")}><CheckCircle2 className="w-4 h-4 mr-2 text-[#0F6B3A]" /> Ver validações</DropdownMenuItem>
                    <DropdownMenuItem data-testid="conf-menu-irregularidades" onClick={() => setMenuOpen("irregularidades")}><AlertTriangle className="w-4 h-4 mr-2 text-[#B91C1C]" /> Irregularidades</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="conf-menu-excluir" onClick={() => { if (boxAtiva) remover(boxAtiva); }} disabled={!boxAtiva || boxAtiva.fechada} className="text-[#B91C1C]"><Trash2 className="w-4 h-4 mr-2" /> Excluir volume atual</DropdownMenuItem>
                    {onLimpar && <DropdownMenuItem data-testid="conf-menu-limpar-nf" onClick={onLimpar} className="text-[#9A6B0A]"><Eraser className="w-4 h-4 mr-2" /> Limpar conferência da NF</DropdownMenuItem>}
                    {onApagar && <DropdownMenuItem data-testid="conf-menu-apagar-nf" onClick={onApagar} className="text-[#B91C1C]"><Trash2 className="w-4 h-4 mr-2" /> Apagar NF-e</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {boxAtiva && !boxAtiva.fechada && unBox > 0 && (
              <button data-testid="conf-enderecar-atual" onClick={() => setFecharBox(boxAtiva)} disabled={busy}
                className="w-full h-12 bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-extrabold text-sm tracking-wide rounded-md flex items-center justify-center gap-2 shadow-md">
                <PackageCheck className="w-5 h-5" /> ENDEREÇAR ESTA CAIXA · {unBox} un
              </button>
            )}
          </div>

          {/* Banner de última caixa endereçada (só aparece após fechar) */}
          {ultimoFechado && (
            <div className="rounded-md border-2 border-[#0F6B3A] bg-[#EAF7EF] p-3 flex items-start gap-2" data-testid="conf-banner-endereco">
              <CheckCircle2 className="w-5 h-5 text-[#0F6B3A] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-[#0B5530] uppercase tracking-wide">Caixa {numeroCaixaDisplay(ultimoFechado.numero)} endereçada</div>
                <div className="mt-0.5 text-sm font-mono font-extrabold text-[#0F2A1F] flex items-center gap-1"><MapPin className="w-4 h-4 text-[#0F6B3A]" /> {ultimoFechado.endereco}</div>
                <div className="text-[10px] text-[#5A6B62] uppercase">{ultimoFechado.destino === "separacao" ? "Separação (fracionada)" : "Pulmão (fechada)"}</div>
              </div>
              <button data-testid="conf-banner-fechar" onClick={() => setUltimoFechado(null)} className="text-[#0B5530] hover:text-[#0F2A1F] shrink-0"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="flex rounded-md overflow-hidden border-2 border-[#2DC26B]">
            <button data-testid="conf-modo-fechada" onClick={() => setModo("fechada")} className={`flex-1 px-3 h-11 text-xs font-bold flex items-center justify-center gap-1 ${modo === "fechada" ? "bg-[#2DC26B] text-white" : "bg-white text-[#0F6B3A]"}`}><Lock className="w-4 h-4" /> Caixa Fechada</button>
            <button data-testid="conf-modo-sortida" onClick={() => setModo("sortida")} className={`flex-1 px-3 h-11 text-xs font-bold flex items-center justify-center gap-1 ${modo === "sortida" ? "bg-[#F2B01E] text-[#0B1F17]" : "bg-white text-[#9A6B0A]"}`}><Shuffle className="w-4 h-4" /> Item a item</button>
          </div>

          {/* Último Registro (padrão coletor — sem endereço durante a bipagem) */}
          <div className="bg-white rounded-lg border border-[#D9E3DC] shadow-sm p-3" data-testid="conf-ultimo-registro">
            <div className="flex items-center justify-between">
              <span className="font-bold text-[#0F2A1F] text-sm uppercase tracking-wide">Último Registro</span>
              <span className="inline-flex items-center gap-1 bg-[#1E4FA3] text-white font-mono font-extrabold px-2.5 py-1 rounded-md text-sm" data-testid="conf-count">
                <PackagePlus className="w-3.5 h-3.5" /> {String(count).padStart(4, "0")}
              </span>
            </div>
            {msg ? (
              <>
                <div className="mt-2.5 text-[#0F2A1F] font-extrabold text-base leading-snug" data-testid="conf-ur-nome">{msg.item.nome}</div>
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-sm text-[#5A6B62]"><b>Cód.</b> {msg.item.codigo}</span>
                  <span className="flex items-center gap-1 text-[#0F6B3A]"><Boxes className="w-4 h-4" /></span>
                  <span className={`inline-flex items-center gap-1 text-white font-extrabold px-3 py-1 rounded-md text-sm shadow ${msg.item.extra ? "bg-[#9A6B0A]" : "bg-[#B91C1C]"}`} data-testid="conf-ur-badge">{msg.item.extra ? "FORA DA NF" : `1 X ${msg.adicionado}un`}</span>
                  <span className="font-extrabold text-[#0F2A1F] text-base"><span className="text-xs font-semibold text-[#5A6B62]">Qtd.</span> {msg.item.conferido}</span>
                </div>
                <div className={`mt-2 text-xs font-bold flex items-center gap-1 ${msg.nivel === "ok" ? "text-[#047857]" : (msg.nivel === "sobra" || msg.nivel === "extra") ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`} data-testid="conf-msg">
                  {msg.nivel === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {msg.mensagem}
                </div>
              </>
            ) : <div className="mt-2 text-sm text-[#8A9A90]">Nenhum item registrado ainda nesta caixa.</div>}
          </div>

          {modo === "sortida" && (
            <div className="flex items-center gap-2" data-testid="conf-qtd-wrap">
              <label className="text-xs font-semibold text-[#5A6B62]">Qtd bipada</label>
              <input data-testid="conf-qtd-input" value={qtd} onChange={(e) => setQtd(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="1"
                className="w-24 h-11 bg-white border border-[#D9E3DC] text-[#0F2A1F] font-mono text-center rounded-md focus:outline-none focus:ring-2 focus:ring-[#2DC26B]" />
              <span className="text-[10px] text-[#8A9A90]">deixe vazio = 1 por bip · digite para validar a quantidade</span>
            </div>
          )}
          <input ref={scanRef} data-testid="conf-scan-input" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); bipar(); } }}
            inputMode="numeric" autoFocus placeholder="Código do item..." className="w-full h-14 bg-white border border-[#D9E3DC] text-[#0F2A1F] font-mono text-xl rounded-md px-4 focus:outline-none focus:ring-2 focus:ring-[#2DC26B]" />
          <button data-testid="conf-bipar-btn" disabled={busy} onClick={bipar} className="w-full h-14 bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-extrabold text-lg tracking-wide rounded-md flex items-center justify-center gap-2"><ScanBarcode className="w-5 h-5" /> BUSCAR</button>
          <Keypad testid="conf-keypad2" onDigit={(d) => setScan((v) => v + d)} onBack={() => setScan((v) => v.slice(0, -1))} onEnter={bipar} />
        </div>
      )}

      {/* Caixas / Localizações já bipadas */}
      {boxes.length > 0 && (
        <div data-testid="conf-bipadas">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] uppercase font-bold text-[#5A6B62] flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Caixas / Localizações bipadas ({boxes.length})</div>
            <input data-testid="conf-busca-caixa" value={buscaCaixa} onChange={(e) => setBuscaCaixa(e.target.value)} placeholder="Consultar caixa..." className="h-8 w-40 text-xs border border-[#D9E3DC] rounded-md px-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" />
          </div>
          <div className="flex flex-wrap gap-2">
            {boxes.filter((b) => !buscaCaixa || String(b.numero).toLowerCase().includes(buscaCaixa.toLowerCase())).map((b) => {
              const un = Object.values(b.itens).reduce((s, q) => s + q, 0);
              const div = caixaDivergente(b);
              return (
                <button key={b.numero} data-testid={`conf-bip-chip-${b.numero}`}
                  onClick={() => { if (b.fechada) setDetalheBox(b); else { setCaixaAtiva(b.numero); setMsg(null); } }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border font-mono text-xs font-bold ${div ? "bg-[#FEF2F2] border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2] ring-1 ring-[#FCA5A5]" : b.fechada ? "bg-[#EAF7EF] border-[#B7E4C7] text-[#0B5530] hover:bg-[#D8F0E2]" : b.numero === caixaAtiva ? "bg-[#0B1F17] border-[#2DC26B] text-[#2DC26B]" : "bg-white border-[#F5E1A8] text-[#9A6B0A] hover:bg-[#FFFDF4]"}`}>
                  {div ? <AlertTriangle className="w-3.5 h-3.5" /> : b.fechada ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Boxes className="w-3.5 h-3.5" />} {numeroCaixaDisplay(b.numero)}
                  <span className="opacity-70">· {Object.keys(b.itens).length} SKU · {un}un</span>
                  {b.fechada && b.endereco && !div && <span className="opacity-70">→ {b.endereco}</span>}
                  {div && <span className="uppercase text-[9px] bg-[#B91C1C] text-white px-1 rounded ml-1" data-testid={`conf-bip-chip-${b.numero}-div`}>DIVERGENTE</span>}
                  {b.fechada && !div && <Eye className="w-3 h-3 opacity-60" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Caixas abertas (detalhe) */}
      {abertas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="conf-abertas">
          {abertas.map((b) => {
            const div = caixaDivergente(b);
            return (
            <div key={b.numero} className={`border rounded-lg p-3 ${div ? "border-[#B91C1C] bg-[#FEF2F2] ring-2 ring-[#FCA5A5]" : b.tipo === "fechada" ? "border-[#B7E4C7] bg-[#F4FBF6]" : "border-[#F5E1A8] bg-[#FFFDF4]"}`} data-testid={`conf-caixa-${b.numero}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono font-bold text-[#0F2A1F] flex items-center gap-1">{div ? <AlertTriangle className="w-3.5 h-3.5 text-[#B91C1C]" /> : b.tipo === "fechada" ? <Lock className="w-3.5 h-3.5 text-[#0F6B3A]" /> : <Shuffle className="w-3.5 h-3.5 text-[#9A6B0A]" />} Caixa {numeroCaixaDisplay(b.numero)}
                  {div && <span className="uppercase text-[9px] bg-[#B91C1C] text-white px-1.5 py-0.5 rounded ml-1 font-extrabold" data-testid={`conf-caixa-${b.numero}-div`}>DIVERGENTE</span>}
                </div>
                <div className="flex gap-1">
                  <button data-testid={`conf-fechar-${b.numero}`} onClick={() => setFecharBox(b)} disabled={busy} className="h-8 px-2.5 rounded-md bg-[#0F6B3A] hover:bg-[#0B5530] text-white text-xs font-bold flex items-center gap-1"><PackageCheck className="w-3.5 h-3.5" /> Endereçar</button>
                  <button data-testid={`conf-remover-${b.numero}`} onClick={() => remover(b)} className="h-8 w-8 rounded-md border border-[#FECACA] text-[#B91C1C] flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="mt-2 space-y-1.5">
                {Object.entries(b.itens).map(([cod, q]) => {
                  const it = rec.itens.find((i) => i.codigo === cod);
                  const diff = it ? (it.qtd_conferida - it.qtd) : 0;
                  const end = enderecoDe(cod);
                  return (
                    <div key={cod} className="text-xs font-mono border-b border-black/5 pb-1 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="truncate"><b className="text-[#0F6B3A]">{cod}</b> <span className="text-[#5A6B62]">{itemNome(cod).slice(0, 22)}</span></span>
                        <span className="shrink-0">{q} un {it && <span className={diff > 0 ? "text-[#B91C1C] font-bold" : diff < 0 ? "text-[#9A6B0A]" : "text-[#047857]"}>({it.qtd_conferida}/{it.qtd})</span>}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-[#5A6B62] mt-0.5"><MapPin className="w-3 h-3 text-[#0F6B3A]" /> {end ? `Endereço: ${end}` : it ? "Sem endereço" : "Fora da NF-e"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Relatório */}
      {showRel && (
        <div className="border-t border-[#F1F5F2] pt-3 space-y-3" data-testid="conf-relatorio">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-bold text-[#0F2A1F]">Relatório de conferência</div>
            <div className="flex gap-2">
              <button data-testid="conf-exportar-pdf" onClick={exportarPdf} className="h-9 px-3 bg-[#0F6B3A] hover:bg-[#0B5530] text-white rounded-md text-xs font-bold flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button data-testid="conf-exportar" onClick={exportar} className="h-9 px-3 bg-white hover:bg-[#F1F5F2] text-[#0F6B3A] border border-[#0F6B3A] rounded-md text-xs font-bold flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> Excel</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
            {[["Caixas fechadas", fechadas.length], ["Volumes (XML)", rec.volumes || "—"], ["Unidades conferidas", totalConf], ["Divergências/faltas", divergentes.length], ["Fora da NF-e", extras.length]].map(([l, v]) => (
              <div key={l} className="bg-[#F4F8F5] rounded-md py-2"><div className="text-[9px] uppercase text-[#8A9A90]">{l}</div><div className="font-mono font-bold text-[#0F2A1F]">{v}</div></div>
            ))}
          </div>
          {/* Totalizadores - Financeiro (Custo) */}
          <div className="overflow-x-auto" data-testid="conf-financeiro">
            <div className="bg-[#1E4FA3] text-white px-2 py-1.5 rounded-t text-xs font-bold flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Totalizadores — Financeiro (Custo)</div>
            <table className="w-full text-xs border border-t-0 border-[#D9E3DC]">
              <thead>
                <tr className="bg-[#F4F8F5] text-[#5A6B62] uppercase text-[10px]">
                  <th className="text-left py-2 px-2 font-bold">Resultados</th>
                  <th className="text-right py-2 px-2 font-bold">Valor estoque</th>
                  <th className="text-right py-2 px-2 font-bold">Valor contagem</th>
                  <th className="text-right py-2 px-2 font-bold">Saldo</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {[
                  ["Total de itens validados", financeiro.cats.validados, "text-[#047857]"],
                  ["Total de itens faltando", financeiro.cats.faltando, "text-[#9A6B0A]"],
                  ["Total de itens sobrando", financeiro.cats.sobrando, "text-[#B91C1C]"],
                  ["Total de itens contagem zero", financeiro.cats.zero, "text-[#5A6B62]"],
                ].map(([label, c, color]) => {
                  const sal = c.cont - c.est;
                  return (
                    <tr key={label} className="border-t border-[#F1F5F2]" data-testid={`conf-fin-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td className={`py-1.5 px-2 font-sans ${color} font-semibold`}>{label}:</td>
                      <td className="py-1.5 px-2 text-right">{brl(c.est)}</td>
                      <td className="py-1.5 px-2 text-right">{brl(c.cont)}</td>
                      <td className={`py-1.5 px-2 text-right font-bold ${sal === 0 ? "text-[#0F2A1F]" : sal > 0 ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`}>{sal > 0 ? "+" : ""}{brl(sal)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-[#0F2A1F] bg-[#F4F8F5]" data-testid="conf-fin-row-total">
                  <td className="py-2 px-2 font-sans font-extrabold text-[#0F2A1F]">Total:</td>
                  <td className="py-2 px-2 text-right font-extrabold text-[#0F2A1F]">{brl(financeiro.total.est)}</td>
                  <td className="py-2 px-2 text-right font-extrabold text-[#0F2A1F]">{brl(financeiro.total.cont)}</td>
                  <td className={`py-2 px-2 text-right font-extrabold ${financeiro.total.cont - financeiro.total.est === 0 ? "text-[#0F2A1F]" : financeiro.total.cont - financeiro.total.est > 0 ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`}>{financeiro.total.cont - financeiro.total.est > 0 ? "+" : ""}{brl(financeiro.total.cont - financeiro.total.est)}</td>
                </tr>
              </tbody>
            </table>
            <div className="text-[9px] text-[#8A9A90] mt-1">Valor unitário obtido da NF-e (vUnCom). Saldo positivo = sobra no estoque; negativo = falta.</div>
          </div>

          {divergentes.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-[10px] uppercase font-bold text-[#B91C1C] mb-1">Divergências e itens não bipados</div>
              <table className="w-full text-xs" data-testid="conf-divergencias">
                <thead><tr className="bg-[#FEF2F2] text-[#B91C1C] uppercase text-[10px] font-bold"><th className="text-left py-2 px-2">Código</th><th className="text-left py-2 px-2">Produto</th><th className="text-right py-2 px-2">XML</th><th className="text-right py-2 px-2">Conf.</th><th className="text-right py-2 px-2">Dif.</th><th className="text-left py-2 px-2">Situação</th><th className="text-left py-2 px-2">Caixas onde foi bipado</th></tr></thead>
                <tbody>
                  {divergentes.map((i) => {
                    const d = i.qtd_conferida - i.qtd;
                    const sit = d > 0 ? "SOBRA" : i.qtd_conferida === 0 ? "NÃO BIPADO" : "FALTA BIPAGEM";
                    return (
                      <tr key={i.codigo} className="border-b border-[#F1F5F2]" data-testid={`conf-div-${i.codigo}`}>
                        <td className="py-1.5 px-2 font-mono font-bold text-[#0F6B3A]">{i.codigo}</td>
                        <td className="py-1.5 px-2 truncate max-w-[200px]">{i.descricao}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{i.qtd}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{i.qtd_conferida}</td>
                        <td className={`py-1.5 px-2 text-right font-mono font-bold ${d > 0 ? "text-[#B91C1C]" : "text-[#9A6B0A]"}`}>{d > 0 ? `+${d}` : d}</td>
                        <td className="py-1.5 px-2"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${d > 0 ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FFF4D6] text-[#9A6B0A]"}`}>{sit}</span></td>
                        <td className="py-1.5 px-2 font-mono text-[10px] text-[#5A6B62]">{cxLabel(i.caixas_conferido) || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {extras.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-[10px] uppercase font-bold text-[#9A6B0A] mb-1">Itens fora da NF-e (bipados, não constam na nota)</div>
              <table className="w-full text-xs" data-testid="conf-extras">
                <thead><tr className="bg-[#FFFBEB] text-[#9A6B0A] uppercase text-[10px] font-bold"><th className="text-left py-2 px-2">Código</th><th className="text-left py-2 px-2">Produto</th><th className="text-right py-2 px-2">Conf.</th><th className="text-left py-2 px-2">Caixas onde foi bipado</th></tr></thead>
                <tbody>
                  {extras.map((e) => (
                    <tr key={e.codigo} className="border-b border-[#F1F5F2]" data-testid={`conf-extra-${e.codigo}`}>
                      <td className="py-1.5 px-2 font-mono font-bold text-[#9A6B0A]">{e.codigo}</td>
                      <td className="py-1.5 px-2 truncate max-w-[220px]">{e.nome}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{e.conferido}</td>
                      <td className="py-1.5 px-2 font-mono text-[10px] text-[#5A6B62]">{cxLabel(e.caixas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {fechadas.length > 0 && (
            <div className="space-y-1" data-testid="conf-fechadas">
              <div className="text-[10px] uppercase font-bold text-[#5A6B62]">Caixas fechadas</div>
              {fechadas.map((b) => (
                <div key={b.numero} className="flex items-center justify-between text-xs font-mono bg-[#F4F8F5] rounded px-2 py-1">
                  <span><b>Caixa {numeroCaixaDisplay(b.numero)}</b> · {b.destino === "separacao" ? "Separação" : "Pulmão"} · {Object.keys(b.itens).length} SKU</span>
                  <span className="text-[#5A6B62]">{b.endereco ? `→ ${b.endereco}` : "sem endereço"} · {b.fechada_em ? fmtDT(b.fechada_em) : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {fecharBox && <FecharDialog box={fecharBox} rec={rec} busy={busy} onClose={() => setFecharBox(null)} onConfirm={confirmarFechar} />}
      {detalheBox && <DetalheCaixaDialog box={detalheBox} rec={rec} onClose={() => setDetalheBox(null)} />}
      {menuOpen && <MenuAcoesDialog kind={menuOpen} rec={rec} boxes={boxes} caixaDivergente={caixaDivergente} onOpenBox={(b) => { setMenuOpen(null); setDetalheBox(b); }} onClose={() => setMenuOpen(null)} />}
    </div>
  );
}

function MenuAcoesDialog({ kind, rec, boxes, caixaDivergente, onOpenBox, onClose }) {
  const fechadas = boxes.filter((b) => b.fechada);
  const abertas = boxes.filter((b) => !b.fechada);
  const divergentes = rec.itens.filter((i) => i.qtd_conferida !== i.qtd);
  const extras = rec.conferencia_extras || [];
  const validadas = boxes.filter((b) => b.validacao?.bipado !== undefined);
  const boxesDiv = boxes.filter((b) => caixaDivergente?.(b));
  const titulos = { volumes: "Ver volumes", enderecamentos: "Ver endereçamentos", validacoes: "Ver validações", irregularidades: "Irregularidades" };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 gap-0 flex flex-col max-h-[85vh]" data-testid={`conf-menu-dialog-${kind}`}>
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-[#F1F5F2] shrink-0">
          <DialogTitle className="text-[#0F2A1F] flex items-center gap-2">
            {kind === "volumes" && <List className="w-5 h-5 text-[#0F6B3A]" />}
            {kind === "enderecamentos" && <MapPin className="w-5 h-5 text-[#0F6B3A]" />}
            {kind === "validacoes" && <CheckCircle2 className="w-5 h-5 text-[#0F6B3A]" />}
            {kind === "irregularidades" && <AlertTriangle className="w-5 h-5 text-[#B91C1C]" />}
            {titulos[kind]}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5A6B62]">NF-e {rec.nf_numero} · {boxes.length} caixa(s) registrada(s)</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2">
          {kind === "volumes" && (
            boxes.length === 0 ? <div className="text-sm text-[#8A9A90] py-6 text-center">Nenhum volume bipado ainda.</div> :
            <>
              {boxesDiv.length > 0 && <div className="text-[10px] uppercase font-bold text-[#B91C1C] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {boxesDiv.length} caixa(s) divergente(s)</div>}
              {boxes.map((b) => {
                const un = Object.values(b.itens).reduce((s, q) => s + q, 0);
                const div = caixaDivergente?.(b);
                return (
                  <button key={b.numero} data-testid={`conf-menu-vol-${b.numero}`} onClick={() => onOpenBox(b)}
                    className={`w-full text-left flex items-center justify-between gap-2 border rounded-md p-2.5 ${div ? "border-[#B91C1C] bg-[#FEF2F2] hover:bg-[#FEE2E2] ring-1 ring-[#FCA5A5]" : b.fechada ? "border-[#B7E4C7] bg-[#F4FBF6] hover:bg-[#EAF7EF]" : "border-[#F5E1A8] bg-[#FFFDF4] hover:bg-[#FFF8E0]"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {div ? <AlertTriangle className="w-4 h-4 text-[#B91C1C]" /> : b.fechada ? <CheckCircle2 className="w-4 h-4 text-[#0F6B3A]" /> : <Boxes className="w-4 h-4 text-[#9A6B0A]" />}
                      <span className={`font-mono font-extrabold ${div ? "text-[#B91C1C]" : "text-[#0F2A1F]"}`}>{numeroCaixaDisplay(b.numero)}</span>
                      <span className="text-xs text-[#5A6B62]">{Object.keys(b.itens).length} SKU · {un}un</span>
                    </div>
                    {div ? <span className="uppercase text-[9px] font-extrabold bg-[#B91C1C] text-white px-1.5 py-0.5 rounded shrink-0">DIVERGENTE</span>
                      : b.fechada && b.endereco ? <span className="font-mono text-xs text-[#0F6B3A] font-bold shrink-0">→ {b.endereco}</span>
                      : !b.fechada ? <span className="text-[10px] font-bold uppercase text-[#9A6B0A] shrink-0">Aberta</span>
                      : null}
                  </button>
                );
              })}
            </>
          )}
          {kind === "enderecamentos" && (
            fechadas.length === 0 ? <div className="text-sm text-[#8A9A90] py-6 text-center">Nenhuma caixa endereçada ainda.</div> :
            fechadas.map((b) => (
              <button key={b.numero} data-testid={`conf-menu-end-${b.numero}`} onClick={() => onOpenBox(b)}
                className="w-full text-left border border-[#B7E4C7] bg-[#F4FBF6] hover:bg-[#EAF7EF] rounded-md p-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-extrabold text-[#0F2A1F] text-sm">Caixa {numeroCaixaDisplay(b.numero)}</span>
                  <span className="text-[10px] uppercase font-bold text-[#0B5530]">{b.destino === "separacao" ? "Separação" : "Pulmão"}</span>
                </div>
                <div className="mt-1 font-mono text-sm font-bold text-[#0F6B3A] flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {b.endereco || "—"}</div>
                <div className="text-[10px] text-[#5A6B62]">{Object.keys(b.itens).length} SKU · {Object.values(b.itens).reduce((s, q) => s + q, 0)}un · {b.fechada_em ? fmtDT(b.fechada_em) : ""}</div>
              </button>
            ))
          )}
          {kind === "validacoes" && (
            validadas.length === 0 ? <div className="text-sm text-[#8A9A90] py-6 text-center">Nenhuma validação física registrada.</div> :
            validadas.map((b) => {
              const v = b.validacao || {};
              const ok = v.diferenca === 0;
              return (
                <button key={b.numero} data-testid={`conf-menu-val-${b.numero}`} onClick={() => b.fechada && onOpenBox(b)}
                  className={`w-full text-left border rounded-md p-2.5 ${ok ? "border-[#B7E4C7] bg-[#F4FBF6]" : "border-[#FECACA] bg-[#FEF2F2]"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-extrabold text-[#0F2A1F]">Caixa {numeroCaixaDisplay(b.numero)}</span>
                    <span className={`text-[10px] uppercase font-bold ${ok ? "text-[#047857]" : "text-[#B91C1C]"} flex items-center gap-1`}>{ok ? <><CheckCircle2 className="w-3 h-3" /> OK</> : <><AlertTriangle className="w-3 h-3" /> Divergência</>}</span>
                  </div>
                  <div className="text-xs font-mono text-[#5A6B62] mt-1">Bipado: {v.bipado} · Encontrado: {v.encontrado ?? "—"} · Diferença: {v.diferenca > 0 ? "+" : ""}{v.diferenca ?? 0}</div>
                </button>
              );
            })
          )}
          {kind === "irregularidades" && (
            divergentes.length === 0 && extras.length === 0 ? <div className="text-sm text-[#8A9A90] py-6 text-center">Nenhuma irregularidade identificada.</div> :
            <>
              {divergentes.length > 0 && <div className="text-[10px] uppercase font-bold text-[#B91C1C]">Divergências ({divergentes.length})</div>}
              {divergentes.map((i) => {
                const d = i.qtd_conferida - i.qtd;
                const sit = d > 0 ? "SOBRA" : i.qtd_conferida === 0 ? "NÃO BIPADO" : "FALTA";
                return (
                  <div key={i.codigo} className="border border-[#FECACA] bg-[#FEF2F2] rounded-md p-2.5" data-testid={`conf-menu-irr-${i.codigo}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><span className="font-mono font-bold text-[#B91C1C]">{i.codigo}</span> <span className="text-xs text-[#5A6B62] truncate">{i.descricao}</span></div>
                      <span className="text-[10px] font-bold text-[#B91C1C] shrink-0">{sit}</span>
                    </div>
                    <div className="text-xs font-mono text-[#5A6B62] mt-1">XML: {i.qtd} · Conf.: {i.qtd_conferida} · Dif.: <b className="text-[#B91C1C]">{d > 0 ? "+" : ""}{d}</b></div>
                  </div>
                );
              })}
              {extras.length > 0 && <div className="text-[10px] uppercase font-bold text-[#9A6B0A] mt-2">Fora da NF-e ({extras.length})</div>}
              {extras.map((e) => (
                <div key={e.codigo} className="border border-[#F5E1A8] bg-[#FFFDF4] rounded-md p-2.5" data-testid={`conf-menu-extra-${e.codigo}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><span className="font-mono font-bold text-[#9A6B0A]">{e.codigo}</span> <span className="text-xs text-[#5A6B62] truncate">{e.nome}</span></div>
                    <span className="text-[10px] font-bold text-[#9A6B0A] shrink-0">{e.conferido} un</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="shrink-0 border-t border-[#D9E3DC] bg-white px-4 py-3">
          <button data-testid="conf-menu-dialog-fechar" onClick={onClose} className="w-full h-11 bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-bold rounded-md">Fechar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetalheCaixaDialog({ box, rec, onClose }) {
  const un = Object.values(box.itens).reduce((s, q) => s + q, 0);
  const validacao = box.validacao || {};
  const isFechada = !!box.fechada;
  const destinoLabel = box.destino === "separacao" ? "Separação" : box.destino === "pulmao" ? "Pulmão" : (isFechada ? "—" : "A endereçar");
  const itens = Object.entries(box.itens).map(([cod, q]) => {
    const it = rec.itens.find((i) => i.codigo === cod);
    const escolhido = it?.enderecos_escolhidos?.find((e) => e && e.length) || (it?.enderecos_atuais?.[0]) || box.endereco || "";
    return { cod, q, nome: it?.descricao || "Fora da NF-e", endereco: escolhido, xml: it?.qtd, conf: it?.qtd_conferida, vunit: it?.vunit || 0, sugestao: !isFechada };
  });
  const totalValor = itens.reduce((s, r) => s + (r.q * (r.vunit || 0)), 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[92vh]" data-testid="conf-detalhe-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-[#F1F5F2] shrink-0">
          <DialogTitle className="text-[#0F2A1F] flex items-center gap-2">
            {isFechada ? <CheckCircle2 className="w-5 h-5 text-[#0F6B3A]" /> : <Boxes className="w-5 h-5 text-[#9A6B0A]" />}
            Caixa {numeroCaixaDisplay(box.numero)} — {isFechada ? "Endereçada" : "Aberta"}
            <span className={`ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${isFechada ? "bg-[#EAF7EF] text-[#0B5530]" : "bg-[#FFFBEB] text-[#9A6B0A]"}`}>{isFechada ? "Fechada" : "Aberta"}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5A6B62]">{isFechada ? "Itens bipados nesta caixa e seus endereços destino." : "Itens já bipados nesta caixa. Endereço final será definido ao endereçar a caixa."}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-[#F4F8F5] rounded p-2"><div className="text-[9px] uppercase text-[#8A9A90]">Destino</div><div className="font-bold text-[#0F2A1F] flex items-center gap-1"><Warehouse className="w-3.5 h-3.5 text-[#0F6B3A]" /> {destinoLabel}</div></div>
            <div className="bg-[#F4F8F5] rounded p-2"><div className="text-[9px] uppercase text-[#8A9A90]">{isFechada ? "Endereço padrão" : "Etiqueta bipada"}</div><div className="font-mono font-bold text-[#0F2A1F]">{box.endereco || numeroCaixaDisplay(box.numero)}</div></div>
            <div className="bg-[#F4F8F5] rounded p-2"><div className="text-[9px] uppercase text-[#8A9A90]">SKUs / Unidades</div><div className="font-mono font-bold text-[#0F2A1F]">{Object.keys(box.itens).length} SKU · {un} un</div></div>
            <div className="bg-[#F4F8F5] rounded p-2"><div className="text-[9px] uppercase text-[#8A9A90]">{isFechada ? "Fechada em" : "Tipo"}</div><div className="font-mono text-[10px] text-[#0F2A1F]">{isFechada ? (box.fechada_em ? fmtDT(box.fechada_em) : "—") : (box.tipo === "fechada" ? "Caixa Fechada" : "Item a item")}</div></div>
          </div>
          {validacao.bipado !== undefined && (
            <div className={`rounded border p-2 text-xs ${validacao.diferenca === 0 ? "border-[#B7E4C7] bg-[#EAF7EF] text-[#047857]" : "border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]"}`} data-testid="conf-detalhe-validacao">
              <div className="font-bold flex items-center gap-1">{validacao.diferenca === 0 ? <><CheckCircle2 className="w-3.5 h-3.5" /> Validação OK</> : <><AlertTriangle className="w-3.5 h-3.5" /> Divergência de validação</>}</div>
              <div className="font-mono mt-1">Bipado: {validacao.bipado} · Encontrado: {validacao.encontrado ?? "—"} · Diferença: {validacao.diferenca > 0 ? "+" : ""}{validacao.diferenca ?? 0}</div>
            </div>
          )}
          <div className="text-[10px] uppercase font-bold text-[#5A6B62]">Produtos {isFechada ? "& Endereços destino" : "bipados (endereço sugerido)"}</div>
          <div className="space-y-1.5">
            {itens.map((r) => (
              <div key={r.cod} className="border border-[#D9E3DC] rounded-md p-2 text-xs" data-testid={`conf-detalhe-item-${r.cod}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-bold text-[#0F6B3A]">{r.cod}</div>
                    <div className="text-[11px] text-[#0F2A1F] font-semibold leading-tight mt-0.5">{r.nome}</div>
                  </div>
                  <span className="font-mono font-extrabold text-[#0F2A1F] shrink-0 bg-[#F4F8F5] px-2 py-1 rounded">{r.q} un</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px]">
                  <span className={`inline-flex items-center gap-1 font-mono font-bold ${r.endereco ? "text-[#047857]" : "text-[#9A6B0A]"}`}>
                    <MapPin className="w-3 h-3" /> {r.endereco || "sem endereço"}{r.sugestao && r.endereco ? " (sugerido)" : ""}
                  </span>
                  {r.vunit ? <span className="font-mono text-[#5A6B62]">R$ {(r.q * r.vunit).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> : <span className="text-[#9A6B0A] font-bold">Fora da NF-e</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="text-right text-xs font-mono text-[#5A6B62]">Valor total da caixa: <b className="text-[#0F2A1F]">R$ {totalValor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></div>
        </div>
        <div className="shrink-0 border-t border-[#D9E3DC] bg-white px-4 py-3">
          <button data-testid="conf-detalhe-fechar" onClick={onClose} className="w-full h-11 bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-bold rounded-md">Fechar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FecharDialog({ box, rec, busy, onClose, onConfirm }) {
  const [destino, setDestino] = useState(box.tipo === "fechada" ? "pulmao" : "separacao");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true; setLoading(true);
    api.get(`/recebimento/${rec.id}/conferencia/${encodeURIComponent(box.numero)}/preview`, { params: { destino } })
      .then(({ data }) => { if (ok) setRows((prev) => data.itens.map((i) => { const old = prev.find((r) => r.codigo === i.codigo); return { ...i, qtd: old ? old.qtd : i.qtd, endereco: i.sugerido || "" }; })); })
      .catch(() => { if (ok) setRows([]); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [destino, rec.id, box.numero]);
  const [encontrado, setEncontrado] = useState("");
  const [showItens, setShowItens] = useState(false);
  const set = (cod, k, v) => setRows((rs) => rs.map((r) => (r.codigo === cod ? { ...r, [k]: v } : r)));
  const totalBipado = rows.reduce((s, r) => s + (Number(r.qtd) || 0), 0);
  const sortidaPulmao = destino === "pulmao" && Object.keys(box.itens).length > 1;
  const confirmar = () => onConfirm({ destino, validacao_qtd: encontrado === "" ? undefined : Number(encontrado), itens: rows.map((r) => ({ codigo: r.codigo, qtd: Number(r.qtd) || 0, endereco: (r.endereco || "").trim() || undefined })) });
  const divergencia = encontrado !== "" ? Number(encontrado) - totalBipado : 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[92vh]" data-testid="conf-fechar-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-[#F1F5F2] shrink-0">
          <DialogTitle className="text-[#0F2A1F]">Endereçamento — Caixa {numeroCaixaDisplay(box.numero)}</DialogTitle>
          <DialogDescription className="text-xs text-[#5A6B62]">Confirme o destino, valide a quantidade física e endereçe a caixa.</DialogDescription>
        </DialogHeader>

        {/* Área rolável */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
          {/* 1. Destino */}
          <div className="flex rounded-md overflow-hidden border-2 border-[#D9E3DC]">
            <button data-testid="conf-fechar-pulmao" onClick={() => setDestino("pulmao")} className={`flex-1 h-11 text-xs font-bold flex items-center justify-center gap-1 ${destino === "pulmao" ? "bg-[#0F6B3A] text-white" : "bg-white text-[#0F2A1F]"}`}><Warehouse className="w-4 h-4" /> Pulmão</button>
            <button data-testid="conf-fechar-separacao" onClick={() => setDestino("separacao")} className={`flex-1 h-11 text-xs font-bold flex items-center justify-center gap-1 ${destino === "separacao" ? "bg-[#F2B01E] text-[#0B1F17]" : "bg-white text-[#0F2A1F]"}`}><Shuffle className="w-4 h-4" /> Separação</button>
          </div>
          {sortidaPulmao && <div className="flex items-center gap-1 text-[11px] font-bold text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md p-2" data-testid="conf-fechar-sortida-alerta"><AlertTriangle className="w-4 h-4" /> Caixa SORTIDA não pode ir para o Pulmão. Use a Separação.</div>}

          {/* 2. Validação da Caixa — em destaque logo no topo */}
          <div className="rounded-md border-2 border-[#0F6B3A] p-3 bg-[#F4F8F5]" data-testid="conf-validacao">
            <div className="flex items-center justify-between text-sm">
              <span className="font-extrabold text-[#0F2A1F] flex items-center gap-1"><PackageCheck className="w-4 h-4 text-[#0F6B3A]" /> Validação da Caixa</span>
              <span className="font-mono text-[#5A6B62]">Bipado: <b className="text-[#0F2A1F] text-base">{totalBipado}</b> un</span>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <label className="text-xs font-semibold text-[#5A6B62]">Qtd física encontrada</label>
              <input data-testid="conf-validacao-input" value={encontrado} onChange={(e) => setEncontrado(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="—" className="w-24 h-11 text-center font-mono text-lg font-bold border border-[#D9E3DC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" />
              {encontrado !== "" && (divergencia === 0
                ? <span className="text-xs font-bold text-[#047857] flex items-center gap-1" data-testid="conf-validacao-result"><CheckCircle2 className="w-4 h-4" /> Validação OK</span>
                : <span className="text-xs font-bold text-[#B91C1C] flex items-center gap-1" data-testid="conf-validacao-result"><AlertTriangle className="w-4 h-4" /> Divergência: {divergencia > 0 ? "+" : ""}{divergencia}</span>)}
            </div>
          </div>

          {/* 3. Detalhes dos itens — colapsável */}
          <div>
            <button data-testid="conf-toggle-itens" onClick={() => setShowItens((v) => !v)}
              className="w-full flex items-center justify-between text-xs font-bold text-[#0F6B3A] uppercase tracking-wide py-1.5 hover:underline">
              <span>{showItens ? "▼" : "►"} Ajustar itens e endereços ({rows.length})</span>
              <span className="font-mono text-[10px] text-[#5A6B62]">{loading ? "..." : "opcional"}</span>
            </button>
            {showItens && (
              <div className="space-y-2 mt-1">
                {loading ? <div className="py-6 text-center text-[#8A9A90] text-sm">Calculando endereçamento inteligente...</div> : rows.map((r) => (
                  <div key={r.codigo} className="border border-[#D9E3DC] rounded-md p-2.5" data-testid={`conf-fechar-row-${r.codigo}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><span className="font-mono font-bold text-[#0F6B3A] text-sm">{r.codigo}</span> <span className="text-xs text-[#5A6B62] truncate">{r.nome}</span></div>
                      <div className="flex items-center gap-1 shrink-0"><span className="text-[10px] text-[#8A9A90]">Qtd bipada</span>
                        <input data-testid={`conf-fechar-qtd-${r.codigo}`} value={r.qtd} onChange={(e) => set(r.codigo, "qtd", e.target.value.replace(/\D/g, ""))} inputMode="numeric" className="w-16 h-9 text-center font-mono border border-[#D9E3DC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" /></div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1 text-[10px]">
                      {r.sem_endereco ? <span className="text-[#B91C1C] font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {r.novo ? "Produto novo — sem endereço, informe manualmente" : "Sem endereço livre — informe manualmente"}</span>
                        : <span className="text-[#047857] flex items-center gap-1"><MapPin className="w-3 h-3" /> Sugerido: <b>{r.sugerido}</b>{r.novo ? " (novo)" : ""}{r.sugestao?.length > 1 || r.enderecos_atuais?.length ? ` · alt.: ${[...new Set([...(r.enderecos_atuais || []), ...(r.sugestao || [])])].slice(0, 3).join(", ")}` : ""}</span>}
                    </div>
                    <input data-testid={`conf-fechar-end-${r.codigo}`} value={r.endereco} onChange={(e) => set(r.codigo, "endereco", e.target.value)} placeholder={`Endereço na ${destino === "pulmao" ? "Pulmão" : "Separação"}`}
                      className="mt-1.5 w-full h-10 bg-white border border-[#D9E3DC] text-[#0F2A1F] font-mono text-sm rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rodapé sticky — sempre visível */}
        <div className="shrink-0 border-t border-[#D9E3DC] bg-white px-4 py-3 space-y-2">
          <button data-testid="conf-fechar-confirmar" disabled={busy || loading || sortidaPulmao} onClick={confirmar}
            className="w-full h-14 bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-extrabold text-base tracking-wide rounded-md flex items-center justify-center gap-2 shadow-md">
            <PackageCheck className="w-5 h-5" /> CONFIRMAR ENDEREÇAMENTO
          </button>
          <button data-testid="conf-fechar-cancelar" onClick={onClose} className="w-full h-9 text-xs font-semibold text-[#5A6B62] hover:text-[#0F2A1F]">Cancelar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
